import React, { FC, useState } from "react";

import { Portal } from "@UI/Components/Portal/Portal";
import { Drawer } from "@UI/Components/Drawer/Drawer";
import { Box } from "@UI/Components/Box/Box";
import { Heading } from "@UI/Components/Heading/Heading";
import { Icon } from "@UI/Components/Icon/Icon";
import { Flex } from "@UI/Components/Flex/Flex";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
} from "@UI/Components/Accordion/Accordion";
import { BrandDots } from "@UI/Components/Icons/BrandDots";
import { signIn } from "@modules/Authentication/services/sessions";
import useCookies from "@Libs/Utils/hooks/useCookies";
import { useHistory } from "@Libs/Router/hooks/useHistory";
import {
  clientsWithManyTasks,
  clientsWithRecurrentTasks,
  inactiveClients,
  serviceProvidersWithManyTasks,
  validatedServiceProviders,
  badServiceProviders,
  serviceProvidersWithRecurrency,
  admins,
} from "../../services/testAccounts";
import { ChevronRight } from "@UI/Components/Icons/ChevronRight";
import { Plus } from "@UI/Components/Icons/Plus";

export const DevMenu: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const cookies = useCookies();
  const history = useHistory();

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const redirectToRoot = () => {
    history.push("/");
    history.go(0);
  };

  const handleLogin = (email) => async () => {
    try {
      const response = await signIn({
        email,
        password: "listminut",
      });
      cookies.set("authToken", response.data.token, {
        path: "/",
      });
    } catch (error) {
      console.error(error);
    }
    redirectToRoot();
  };

  return (
    <Portal id="dev-tools">
      <Box
        position="fixed"
        bottom="3"
        left="16"
        zIndex="10000"
        _hover={{ cursor: "pointer" }}
        onClick={handleOpen}
      >
        <Icon as={BrandDots} height="8" width="8" stroke="black" />
      </Box>
      <Drawer placement="right" isOpen={isOpen} onClose={handleClose} size="lg">
        <Heading marginBottom="5">Login with:</Heading>
        <Accordion fontSize="sm">
          <AccordionItem>
            <AccordionButton
              color="gray.600"
              as={Flex}
              justifyContent="space-between"
            >
              A client with a lot of tasks
              <Icon as={Plus} />
            </AccordionButton>
            <AccordionPanel>
              {clientsWithManyTasks.map((email) => (
                <Flex
                  justifyContent="space-between"
                  cursor="pointer"
                  _hover={{
                    background: "gray.50",
                  }}
                  alignItems="center"
                  padding="3"
                  key={email}
                  onClick={handleLogin(email)}
                >
                  {email}
                  <Icon as={ChevronRight} marginLeft="3" />
                </Flex>
              ))}
            </AccordionPanel>
          </AccordionItem>
          <AccordionItem>
            <AccordionButton
              color="gray.600"
              as={Flex}
              justifyContent="space-between"
            >
              A client with recurrent tasks
              <Icon as={Plus} />
            </AccordionButton>
            <AccordionPanel>
              {clientsWithRecurrentTasks.map((email) => (
                <Flex
                  justifyContent="space-between"
                  cursor="pointer"
                  _hover={{
                    background: "gray.50",
                  }}
                  alignItems="center"
                  padding="3"
                  key={email}
                  onClick={handleLogin(email)}
                >
                  {email}
                  <Icon as={ChevronRight} marginLeft="3" />
                </Flex>
              ))}
            </AccordionPanel>
          </AccordionItem>
          <AccordionItem>
            <AccordionButton
              color="gray.600"
              as={Flex}
              justifyContent="space-between"
            >
              An inactive client
              <Icon as={Plus} />
            </AccordionButton>
            <AccordionPanel>
              {inactiveClients.map((email) => (
                <Flex
                  justifyContent="space-between"
                  cursor="pointer"
                  _hover={{
                    background: "gray.50",
                  }}
                  alignItems="center"
                  paddingY="3"
                  key={email}
                  onClick={handleLogin(email)}
                >
                  {email}
                  <Icon as={ChevronRight} marginLeft="3" />
                </Flex>
              ))}
            </AccordionPanel>
          </AccordionItem>
          <AccordionItem>
            <AccordionButton
              color="gray.600"
              as={Flex}
              justifyContent="space-between"
            >
              An SP with many tasks
              <Icon as={Plus} />
            </AccordionButton>
            <AccordionPanel>
              {serviceProvidersWithManyTasks.map((email) => (
                <Flex
                  justifyContent="space-between"
                  cursor="pointer"
                  _hover={{
                    background: "gray.50",
                  }}
                  alignItems="center"
                  padding="3"
                  key={email}
                  onClick={handleLogin(email)}
                >
                  {email}
                  <Icon as={ChevronRight} marginLeft="3" />
                </Flex>
              ))}
            </AccordionPanel>
          </AccordionItem>
          <AccordionItem>
            <AccordionButton
              color="gray.600"
              as={Flex}
              justifyContent="space-between"
            >
              A validated SP
              <Icon as={Plus} />
            </AccordionButton>
            <AccordionPanel>
              {validatedServiceProviders.map((email) => (
                <Flex
                  justifyContent="space-between"
                  cursor="pointer"
                  _hover={{
                    background: "gray.50",
                  }}
                  alignItems="center"
                  padding="3"
                  key={email}
                  onClick={handleLogin(email)}
                >
                  {email}
                  <Icon as={ChevronRight} marginLeft="3" />
                </Flex>
              ))}
            </AccordionPanel>
          </AccordionItem>
          <AccordionItem>
            <AccordionButton
              color="gray.600"
              as={Flex}
              justifyContent="space-between"
            >
              A bad SP
              <Icon as={Plus} />
            </AccordionButton>
            <AccordionPanel>
              {badServiceProviders.map((email) => (
                <Flex
                  justifyContent="space-between"
                  cursor="pointer"
                  _hover={{
                    background: "gray.50",
                  }}
                  alignItems="center"
                  padding="3"
                  key={email}
                  onClick={handleLogin(email)}
                >
                  {email}
                  <Icon as={ChevronRight} marginLeft="3" />
                </Flex>
              ))}
            </AccordionPanel>
          </AccordionItem>
          <AccordionItem>
            <AccordionButton
              color="gray.600"
              as={Flex}
              justifyContent="space-between"
            >
              An SP with recurrent tasks
              <Icon as={Plus} />
            </AccordionButton>
            <AccordionPanel>
              {serviceProvidersWithRecurrency.map((email) => (
                <Flex
                  justifyContent="space-between"
                  cursor="pointer"
                  _hover={{
                    background: "gray.50",
                  }}
                  alignItems="center"
                  padding="3"
                  key={email}
                  onClick={handleLogin(email)}
                >
                  {email}
                  <Icon as={ChevronRight} marginLeft="3" />
                </Flex>
              ))}
            </AccordionPanel>
          </AccordionItem>
          <AccordionItem>
            <AccordionButton
              color="gray.600"
              as={Flex}
              justifyContent="space-between"
            >
              An admin
              <Icon as={Plus} />
            </AccordionButton>
            <AccordionPanel>
              {admins.map((email) => (
                <Flex
                  justifyContent="space-between"
                  cursor="pointer"
                  _hover={{
                    background: "gray.50",
                  }}
                  alignItems="center"
                  padding="3"
                  key={email}
                  onClick={handleLogin(email)}
                >
                  {email}
                  <Icon as={ChevronRight} marginLeft="3" />
                </Flex>
              ))}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Drawer>
    </Portal>
  );
};
